/**
 * Opens Paddle checkout for a transaction the server created (firm-model spec §7): the
 * Paddle.js overlay when this build has a client-side token, otherwise the transaction's
 * hosted checkout URL (the slidebrain.co checkout page, which opens the same overlay).
 */
import { env } from "../env";

type PaddleGlobal = {
  Environment: { set(env: string): void };
  Initialize(opts: { token: string }): void;
  Checkout: { open(opts: { transactionId: string }): void };
};

const PADDLE_JS = "https://cdn.paddle.com/paddle/v2/paddle.js";

let ready: Promise<PaddleGlobal> | null = null;

function loadPaddle(): Promise<PaddleGlobal> {
  if (!ready) {
    ready = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = PADDLE_JS;
      script.async = true;
      script.onload = () => {
        const paddle = (window as unknown as { Paddle?: PaddleGlobal }).Paddle;
        if (!paddle) return reject(new Error("Paddle.js didn't load"));
        if (env.paddleEnv === "sandbox") paddle.Environment.set("sandbox");
        paddle.Initialize({ token: env.paddleClientToken });
        resolve(paddle);
      };
      script.onerror = () => {
        ready = null;
        reject(new Error("Paddle.js didn't load"));
      };
      document.head.appendChild(script);
    });
  }
  return ready;
}

/** How this build opens a checkout: pure, so it's tested. */
export function checkoutMode(transaction: { url: string | null }, token: string): "overlay" | "redirect" | "unavailable" {
  if (token) return "overlay";
  return transaction.url ? "redirect" : "unavailable";
}

export async function openCheckout(transaction: { transactionId: string; url: string | null }): Promise<void> {
  const mode = checkoutMode(transaction, env.paddleClientToken);
  if (mode === "overlay") {
    const paddle = await loadPaddle();
    paddle.Checkout.open({ transactionId: transaction.transactionId });
    return;
  }
  if (mode === "redirect" && transaction.url) {
    window.location.assign(transaction.url);
    return;
  }
  throw new Error("Checkout isn't available right now. Try again later.");
}
